import { type FC, type MutableRefObject, useEffect, useImperativeHandle, useMemo } from "react";
import { useFormik } from "formik";
import { noop } from "lodash";

import type ColumnOptions from "../../../../../interfaces/columnOptions";
import { type LicenseConfirmationItem } from "../../../../../interfaces";
import { TextAlign } from "../../../../../enums/textAlign";
import { PricingTypes } from "../../../../../enums/licensing/pricingTypes";

import { Label } from "../../../../../components/common/label/Label";
import { Typography } from "../../../../../components/common/Typography/Typography";
import { Tooltip } from "../../../../../components/common/tooltip";
import { ValidatedForm } from "../../../../../components/forms";
import {
  getLabelOption,
  calculateTotals,
  formatNumber,
  formatUsedLicenses,
  getInitialValues,
  type LicenseConfirmationFormItem,
  getColumns,
  computeIssuingLicenses,
  needsLicensePurchase,
} from "./utils";
import { licenseConfirmationValidationSchemas } from "../../../../../utils/validationSchemas/licenseConfirmationValidationSchemas";
import { withUSFormattedNumberInput } from "../../../../../components/forms/USFormattedNumberInput/USFormattedNumberInput";
import InputField from "../../../../../components/forms/InputField/InputField";

import styles from "./licenseConfirmationBlock.module.scss";

export type LicenseConfirmationFormRef = {
  getValues: () => LicenseConfirmationFormItem[];
};

export interface LicenseConfirmationBlockProps {
  items: LicenseConfirmationItem[];
  licenseConfirmationFormRef?: MutableRefObject<LicenseConfirmationFormRef>;
  onIsDataValidChange?: (isValid: boolean) => void;
  isPurchaseNeeded: boolean;
}

const USFormattedInputField = withUSFormattedNumberInput(InputField);

export const LicenseConfirmationBlock: FC<LicenseConfirmationBlockProps> = ({
  items,
  licenseConfirmationFormRef,
  onIsDataValidChange,
  isPurchaseNeeded,
}) => {
  const initialValues = useMemo(() => getInitialValues(items), [items]);
  const formik = useFormik<LicenseConfirmationFormItem[]>({
    validationSchema: useMemo(() => licenseConfirmationValidationSchemas(initialValues), [initialValues]),
    initialValues,
    enableReinitialize: true,
    onSubmit: noop,
  });

  const { values, isValid } = formik;

  useImperativeHandle(licenseConfirmationFormRef, () => ({ getValues: () => values }), [values]);

  const hasExhaustedOtherPack = useMemo(() => {
    return items.some((item) => {
      const isOther = item.packPricingTypeId === PricingTypes.Other;
      const isExhausted = item.totalLicenses && item.totalUsedLicenses > item.totalLicenses;
      return isOther && isExhausted;
    });
  }, [items]);

  useEffect(() => {
    onIsDataValidChange?.(isValid && !hasExhaustedOtherPack);
  }, [isValid, onIsDataValidChange, hasExhaustedOtherPack]);

  const totalAmountRow = useMemo(() => {
    const totals = calculateTotals(values);
    const base = [{ value: "TOTAL" }, { value: formatNumber(totals.issuingTotal), textAlign: TextAlign.Center }, {}];
    return isPurchaseNeeded
      ? [...base, { value: formatNumber(totals.licensesCount), textAlign: TextAlign.Center }]
      : base;
  }, [values, isPurchaseNeeded]);

  const buildTableRow = (item: LicenseConfirmationFormItem, index: number) => {
    const labelOption = getLabelOption(item);

    return (
      <>
        <td className={styles.block}>
          <Typography variant="title2">{item.title}</Typography>
          {labelOption && (
            <Tooltip
              showAlways={!!labelOption.tooltip}
              position="top center"
              target={<Label text={labelOption.name} color={labelOption.color} />}
              content={labelOption.tooltip}
            />
          )}
        </td>
        <td align="center">
          <Typography variant="title2">{formatNumber(computeIssuingLicenses(item))}</Typography>
        </td>
        <td align="center">
          <Typography variant="title2">{formatUsedLicenses(item)}</Typography>
        </td>
        {isPurchaseNeeded && (
          <td className={styles.purchasingCell}>
            {needsLicensePurchase(item) ? (
              <USFormattedInputField
                {...formik}
                propertyName={`[${index}].licensesCount`}
                value={item.licensesCount}
                onValueChanged={(data: { value: number | null }) =>
                  formik.setFieldValue(`[${index}].licensesCount`, data.value)
                }
                markAsRequired
                errorPosition="left"
              />
            ) : (
              <Typography variant="title2" className={styles.emptyCell}>
                -
              </Typography>
            )}
          </td>
        )}
      </>
    );
  };

  const columns = useMemo(() => getColumns(isPurchaseNeeded), [isPurchaseNeeded]);
  return (
    <div className={styles.root} data-testid="license-confirmation-block">
      <ValidatedForm withoutFormik fullsize>
        <table className={styles.table} data-testid="license-table">
          <thead>
            <tr key="header-row" data-testid="table-header-row">
              {columns.map((c: ColumnOptions) => (
                <th key={c.name} align={c.textAlign}>
                  <Typography variant="paragraph" bold>
                    {c.name.toUpperCase()}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {values.map((item, index) => (
              <tr key={item.packId} data-testid="table-row">
                {buildTableRow(item, index)}
              </tr>
            ))}
            <tr key="empty-row" className={styles.emptyRow}>
              <td colSpan={columns.length} />
            </tr>
            <tr key="total-row" className={styles.totalAmountRow} data-testid="total-row">
              {totalAmountRow.map((item, index) => (
                <td key={index} align={item.textAlign}>
                  <Typography variant="title1" bold>
                    {item.value}
                  </Typography>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </ValidatedForm>
    </div>
  );
};
